<template lang="pug">
div(:class="[hideAdmin ? 'av-hide-admin' : '', 'layout-container']")
  CampaignBanner(
    v-if="shouldShowCampaignBanner"
    :campaign-copy="activeCampaignCopy")
  NavHeader(v-if="page && page.id")
  LazyPartnerBanner(
    v-if="hasPartnerBanner"
    :partner-ident="partnerIdent"
  )

  Transition(name="ease-in-out" duration="200")
    section.bg-anyvan-semantic-positive(v-if="discountCode.isSet && discountCode.value")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.discount_value.you_have")}} {{ discountCode.value }} {{$t("banner.discount_value.off booking")}}
    section.bg-anyvan-semantic-positive(v-else-if="discountCode.isSet && !discountCode.value")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.discount_value.default")}}
    section.bg-anyvan-semantic-positive(v-else-if="creditCodeValue > 0")
      .content-narrow
        p.av-paragraph.text-center 🎉 {{$t("banner.default.your")}} {{creditCodeCurrency}}{{creditCodeValue}} {{$t("banner.default.discount_applied")}}
  LazyActivePrelisting(
    v-if="shouldShowActivePrelisting"
    :listings="activePrelistings"
    :show-active-prelistings="showActivePrelistings"
    @banner-close="handleActivePrelistingClose")
  section(
    v-for="(block, key) in blocks"
    :key="`block${key}`"
    v-bind="getAttributes(block)"
    :id="`section-no-${key}`")
    .content
      Block(
        :block="block"
        :block-id="`block-no-${key}`"
        :lazy="key > 2"
        virgin
        :isRebrand="true"
        :isPPC="page?.seo?.metaRobotsNoindex === 'noindex'")
  LiveChatWidget(
    v-if="shouldDisplayLiveChat"
    :license="`${$config.public.liveChatLicense}`"
    visibility="minimized"
    group="19")
  TheFooterContainer(v-if="page && page.id" :isRebrand="true")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { mapActions, mapGetters } from "vuex"
import { LiveChatWidget } from "@livechat/widget-vue/v2"
import { getPartnerIdentFromClassName } from "~/constants/partners"
import { getAttributes, getBlockAttribute } from "~/utils/block"
import { GET_INVENTORY, SET_INVENTORY, SET_WINDOW_WIDTH } from "~/store/pages"
import { GET_CREDIT_CODE_VALUE, GET_CREDIT_CODE_CURRENCY } from "~/store/credit-code"
import { campaignIds } from "~/constants/campaigns"
import { URL_PARAM_HIDE_ADMIN } from "~/constants/booking-insights"
import { type AnyVanLocale, LOCALE } from "~/constants/locale"
import {
  getPath,
  isResponseIQExcludePage,
  getv4LocaleFromI18nLocale
} from "~/scripts/useful-functions"
import { SET_PARTNER_IDENT } from "~/store/partner"

type ActiveCampaign = {
  id: string
  supportedLocales: AnyVanLocale[]
  copy: {
    emoji: string
    mobile: {
      text: string
      cta?: string
    }
    desktop: {
      text: string
      cta?: string
    }
  }
  endDate: string
}

interface ComponentData {
  blocks: Block[]
  hideAdmin: boolean
  isKeyboardVisible: boolean
  isAndroid: boolean
  partnerIdent: string | null
  discountCode: {
    isSet: boolean
    value: string | undefined
  }
  activeCampaigns: ActiveCampaign[]
  activePrelistings: Record<string, any>[]
  showActivePrelistings: boolean
  activePrelistingLimit: number
  activePrelistingCategory: number
}

type ValidationResult = {
  data: string | undefined
  error: string | undefined
}

export default defineComponent({
  name: "PageLayoutRebrand",

  components: {
    LiveChatWidget
  },

  props: {
    page: {
      type: Object as () => Page,
      required: true
    }
  },

  data(): ComponentData {
    return {
      blocks: [],
      hideAdmin: false,
      isKeyboardVisible: false,
      isAndroid: false,
      partnerIdent: null,
      discountCode: {
        isSet: false,
        value: undefined
      },
      activeCampaigns: [
        {
          id: campaignIds["christmas-urgency-messaging-banner"],
          supportedLocales: [LOCALE.UK],
          copy: {
            emoji: "🎄",
            mobile: {
              text: "Last spaces on our Xmas sleigh are flying fast! 🛷"
            },
            desktop: {
              text: "Things to move this Christmas? There’s still spaces on our sleigh, but they're flying fast! 🛷"
            }
          },
          endDate: "2026-01-01T00:00:00Z"
        },
        {
          id: campaignIds["october-urgency-messaging-banner"],
          supportedLocales: [LOCALE.ES],
          copy: {
            emoji: "👻",
            mobile: {
              text: "No tengas miedo, tenemos disponibilidad en Octubre"
            },
            desktop: {
              text: "¿Te mudas en Octubre? ¡No tengas miedo! Aún tenemos disponibilidad… ¡pero se van rápido!"
            }
          },
          endDate: "2025-11-01T01:00:00Z"
        }
      ],
      activePrelistings: [],
      showActivePrelistings: true,
      activePrelistingLimit: 2,
      activePrelistingCategory: 0
    }
  },

  computed: {
    ...mapGetters("pages", {
      getInventory: GET_INVENTORY
    }),
    ...mapGetters("credit-code", {
      creditCodeValue: GET_CREDIT_CODE_VALUE,
      creditCodeCurrency: GET_CREDIT_CODE_CURRENCY
    }),

    shouldDisplayLiveChat(): boolean {
      return Boolean(
        this.$config.public.liveChatLicense &&
          this.getInventory === false &&
          this.$i18n.locale === LOCALE.UK
      )
    },

    shouldShowActivePrelisting(): boolean {
      return this.activePrelistings.length > 0 && this.showActivePrelistings
    },

    getActiveCampaignForLocale(): ActiveCampaign | undefined {
      const now = new Date()

      return this.activeCampaigns.find(
        campaign =>
          campaign.supportedLocales.includes(this.$i18n.locale as AnyVanLocale) &&
          now.getTime() < new Date(campaign.endDate).getTime()
      )
    },
    activeCampaignCopy(): {
      id: string
      emoji: string
      mobile: { text: string; cta: string }
      desktop: { text: string; cta: string }
    } | null {
      const campaign = this.getActiveCampaignForLocale
      if (!campaign) {
        return null
      }
      return {
        id: campaign.id,
        emoji: campaign.copy.emoji,
        mobile: campaign.copy.mobile,
        desktop: campaign.copy.desktop
      }
    },

    shouldShowCampaignBanner(): boolean {
      return this.getActiveCampaignForLocale !== undefined
    },

    hasPartnerBanner(): boolean {
      return this.partnerIdent !== null
    }
  },

  watch: {
    getInventory: {
      handler(newVal) {
        if (newVal === false) {
          if (!isResponseIQExcludePage(this.$i18n.locale, getPath(this.$route))) {
            // @ts-ignore-next-line
            window.dataLayer?.push({ event: "show_responseiq" })
          }
        }
      }
    }
  },

  created() {
    this.blocks = [...this.page?.blocks]
    this.hideAdmin = this.$route.query[URL_PARAM_HIDE_ADMIN] === "true"

    const className = this.getBlockAttribute(this.blocks[0]?.attributes ?? [], "className")
    if (className) {
      this.partnerIdent = getPartnerIdentFromClassName(className)
      if (this.partnerIdent) {
        this.setPartnerIdent(this.partnerIdent)
      }
    }

    this.discountCode = {
      isSet: Object.hasOwn(this.$route.query, "discount-value"),
      value: this.validatePercentage(this.$route.query?.["discount-value"]).data
    }
  },

  beforeMount() {
    this.$store.dispatch(`pages/${SET_WINDOW_WIDTH}`, window.innerWidth)
  },

  async mounted() {
    if (this.getInventory === undefined) {
      this.$store.dispatch(`pages/${SET_INVENTORY}`, false)
    }

    // Fetch active prelistings
    await this.fetchActivePrelistings()

    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes("android")) {
      this.isAndroid = true
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", this.updateKeyboardVisibility)
      }
    }
  },
  beforeUnmount() {
    // Clean up the event listener when the component is destroyed
    if (this.isAndroid) {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", this.updateKeyboardVisibility)
      }
    }
  },

  methods: {
    ...mapActions("partner", {
      setPartnerIdent: SET_PARTNER_IDENT
    }),
    getAttributes,
    getBlockAttribute,

    async fetchActivePrelistings() {
      if (!useCookie("active_prelistings_banner").value) {
        try {
          const url =
            this.$config.public.v4Host +
            "/ng/getactiveprelistings?load_count=" +
            this.activePrelistingLimit
          const result = await this.$axios.get(url, {
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              "AV-locale": getv4LocaleFromI18nLocale(this.$i18n.locale)
            },
            withCredentials: true
          })
          let listings = result.data?.listings ?? []
          if (this.activePrelistingCategory) {
            listings = listings.filter((listing: Record<string, any>) => {
              return listing.category_id == this.activePrelistingCategory
            })
          }
          this.activePrelistings = listings
        } catch (e) {
          console.log("There was an error fetching active prelistings", e)
        }
      }
    },

    handleActivePrelistingClose() {
      this.showActivePrelistings = false
    },

    updateKeyboardVisibility() {
      if (window.visualViewport) {
        // Check if the keyboard is visible
        this.isKeyboardVisible = window.visualViewport.height < screen.height * 0.6

        if (this.isKeyboardVisible && this.isAndroid) {
          document.body.classList.add("hide-RIQ")
        } else {
          document.body.classList.remove("hide-RIQ")
        }
      }
    },
    validatePercentage(input: unknown): ValidationResult {
      if (typeof input !== "string") {
        return {
          data: undefined,
          error: "Input must be a string"
        }
      }

      if (!input.endsWith("%")) {
        return {
          data: undefined,
          error: 'Must end with "%"'
        }
      }

      const withoutPercent = input.slice(0, -1)

      if (withoutPercent.includes(".")) {
        return {
          data: undefined,
          error: "Must be an integer"
        }
      }

      const numericValue = Number(withoutPercent)

      if (isNaN(numericValue)) {
        return {
          data: undefined,
          error: "Must be a valid number"
        }
      }

      if (!Number.isInteger(numericValue)) {
        return {
          data: undefined,
          error: "Must be an integer"
        }
      }

      if (numericValue < 0 || numericValue > 100) {
        return {
          data: undefined,
          error: "Must be between 0 and 100"
        }
      }

      return {
        data: `${numericValue}%`,
        error: undefined
      }
    }
  }
})
</script>

<style>
@import "assets/css/rebrand.css";
@import "assets/css/navigation.css";
</style>
